import _, {filter, keyBy, pickBy} from 'lodash';
import {createSelector} from 'reselect';

import {isSidePanelIntegration} from '../../helpers/integrationHelpers';
import {isGlobalAdmin, isTeamAdmin} from '../../helpers/models/accessHelpers';
import {isRecurringMasterEvent, retrieveMasterEventForId} from '../../helpers/models/recurringEventHelpers';
import {AppParametricSelector, AppSelector} from '../../helpers/reselectHelpers';
import {ActivityModel} from '../../models/api/activityModel';
import {CalendarEventVisualStatusesEnum} from '../../models/api/calendarEventModel';
import {AnnotationCommentModel, CommentModel, RAnnotationComment} from '../../models/api/commentModel';
import {ConferenceTypesEnum} from '../../models/api/conferenceModel';
import {ConversationModel} from '../../models/api/conversationModel';
import {CustomFieldModel} from '../../models/api/customFieldModel';
import {isTimeAboveMaxDismissal} from '../../models/api/integrationErrorModel';
import {MessageModel} from '../../models/api/messageModel';
import {AppState} from '../appState';
import {ResourceMap} from '../appTypes';
import {CalendarEventData} from '../panels/calendars/calendarsPanelSelectors';
import {getDismissedIntegrationErrors} from './localPreferences/localPreferencesSelectors';

/*
 * Data.
 */

/** The data state typically used when persisting, fetching, synchronizing, etc. Does not include optimistic updates. */
export function getOriginalData(state: AppState) {
  return state.originalData;
}

/** The data state typically used to display the UI. Includes optimistic updates for in-flight actions. */
export function getOptimisticData(state: AppState) {
  return state.data;
}

/*
 * Config.
 */

export function getPushConfig(state: AppState) {
  return state.data.config.push;
}

export function getChatPushConfig(state: AppState) {
  return state.data.config.chatPush;
}

export function getHelpChatConfig(state: AppState) {
  return state.data.config.helpChat;
}

export function getHost(state: AppState) {
  return state.data.config.host;
}

export function getAllowedDomain(state: AppState) {
  return state.data.config.allowedDomainForEmbed;
}

export function getLanguage(state: AppState) {
  return state.data.config.language;
}

export function getIsImpersonating(state: AppState) {
  return state.data.config.isImpersonating;
}

/*
 * Counters.
 */

export function getCounters(state: AppState) {
  return state.data.counters;
}

export function getBoldCounters(state: AppState) {
  return state.data.counters.bold;
}

/*
 * Access.
 */

export function getAccess(state: AppState) {
  return state.data.access;
}

/*
 * Roles.
 */

export function getRoles(state: AppState) {
  return state.data.roles;
}

/*
 * Accounts.
 */

export function getAccounts(state: AppState) {
  return state.data.accounts;
}

export const getAccountsFlat = createSelector(getAccounts, (accounts) =>
  _(accounts).values().compact().value(),
);

/*
 * Contacts.
 */

export function getContacts(state: AppState) {
  return state.data.contacts;
}

export const getFlatContacts = createSelector(getContacts, (contacts) =>
  _(contacts).values().compact().value(),
);

/*
 * Cards.
 */

export function getCards(state: AppState) {
  return state.data.cards;
}

export const getFlatCards = createSelector(getCards, (cards) => _(cards).values().compact().value());

export function getCardGroups(state: AppState) {
  return state.data.cardGroups;
}

export function getCardNotes(state: AppState) {
  return state.data.cardNotes;
}

/*
 * Conversations.
 */

type ConversationMapSelector = AppSelector<ResourceMap<ConversationModel>>;

/** Returns a map of all conversations. */
export const getConversations: ConversationMapSelector = (state) => state.data.conversations.byId;

/** Returns a map of all conversations without any optimistic updates. */
export const getOriginalConversations: ConversationMapSelector = (state) =>
  state.originalData.conversations.byId;

/*
 * Activities.
 */

export function getAllActivities(state: AppState) {
  return state.data.activities;
}

export const getActivities: AppSelector<ResourceMap<ActivityModel>> = createSelector(
  getAllActivities,
  (allActivities) => pickBy(allActivities, (activity) => activity && activity.deletedAt === undefined),
);

/*
 * Messages.
 */

export function getMessages(state: AppState): ResourceMap<MessageModel> {
  return state.data.messages;
}

/*
 * Comments.
 */

export function getComments(state: AppState): ResourceMap<CommentModel> {
  return state.data.comments.byId;
}

const getAnnotationComments: AppSelector<ReadonlyArray<AnnotationCommentModel>> = createSelector(
  getComments,
  (comments) => filter(comments, RAnnotationComment.is),
);

export const getReferencingCommentUidsMap = createSelector(
  getAnnotationComments,
  getActivities,
  (annotationComments, activities) =>
    _(annotationComments)
      .filter((c) => Boolean(!c.annotation.commentActivityId || activities[c.annotation.commentActivityId]))
      .groupBy((c) => c.referencedActivityId)
      .mapValues((cs) => cs.map((c) => c.uid))
      .value(),
);

/*
 * Calendar Events.
 */

export function getCalendarEvents(state: AppState) {
  return state.data.calendarEvents;
}

export function getCalendarEventVersions(state: AppState) {
  return state.data.calendarEventVersions;
}

export const getMasterCalendarEventIdToUidMap = createSelector(getCalendarEvents, (calendarEvents) =>
  _(calendarEvents)
    .values()
    .compact()
    .map((c) => isRecurringMasterEvent(c) && c.visualStatus === CalendarEventVisualStatusesEnum.COMPLETE && c)
    .compact()
    .keyBy((c) => c.id)
    .mapValues((c) => c.uid)
    .value(),
);

export function makeGetMasterCalendarEventVersion(
  getCalendarEventData: AppParametricSelector<any, CalendarEventData>,
) {
  return createSelector(
    [getCalendarEventData, getMasterCalendarEventIdToUidMap, getCalendarEvents, getCalendarEventVersions],
    (focusedCalendarEventData, masterCalendarEventIdToUidMap, calendarEvents, calendarEventVersions) => {
      const {calendarEvent, calendarVersion} = focusedCalendarEventData;

      if (!calendarEvent.recurringCalendarEventId) {
        return undefined;
      }

      try {
        const [, calendarEventVersion] = retrieveMasterEventForId(
          masterCalendarEventIdToUidMap,
          calendarEvents,
          calendarEventVersions,
          calendarEvent.recurringCalendarEventId,
          calendarVersion.id,
        );
        return calendarEventVersion;
      } catch (error) {
        return undefined;
      }
    },
  );
}

/*
 * Calendars.
 */

export function getCalendars(state: AppState) {
  return state.data.calendars;
}

/*
 * Calendar Versions.
 */

export function getCalendarVersions(state: AppState) {
  return state.data.calendarVersions;
}

/*
 * Scheduling Links.
 */

export function getSchedulingLinks(state: AppState) {
  return state.data.schedulingLinks.byId;
}

/*
 * Integrations.
 */

/** Get a map of all integrations. */
export function getIntegrations(state: AppState) {
  return state.data.integrations;
}

/** Get an array of all integrations. */
export const getFlatIntegrations = createSelector(getIntegrations, (integrations) =>
  _(integrations)
    .values()
    .compact()
    .sortBy((i) => i.type)
    .value(),
);

/** Get a map of all integrations by type. */
export const getIntegrationsByType = createSelector(getFlatIntegrations, (integrations) =>
  keyBy(integrations, (i) => i.type),
);

/** Get an array of all enabled integrations. */
export const getEnabledIntegrations = createSelector(getFlatIntegrations, (integrations) =>
  integrations.filter((i) => i.isEnabled),
);

export const getEnabledIntegrationsIds = createSelector(getEnabledIntegrations, (integrations) =>
  integrations.map((i) => i.type),
);

export const getAttachmentActionIntegrations = createSelector(getEnabledIntegrations, (integrations) =>
  integrations.filter((i) => i.hasAttachmentExtensions),
);

export const getPluginIntegrations = createSelector(getEnabledIntegrations, (integrations) =>
  integrations.filter(isSidePanelIntegration),
);

export const getSideFrameIntegrations = createSelector(getEnabledIntegrations, (integrations) =>
  integrations.filter((i) => !i.isVirtual && !i.plugin && isSidePanelIntegration(i)),
);

export const getConferencingIntegrations = createSelector(getEnabledIntegrations, (integrations) =>
  integrations.filter((i) => i.type === ConferenceTypesEnum.ZOOM),
);

/*
 * Integration errors.
 */

export function getIntegrationErrors(state: AppState) {
  return state.data.integrationErrors;
}

export const getActiveIntegrationErrors = createSelector(
  [getIntegrationErrors, getDismissedIntegrationErrors, getAccess],
  (integrationErrors, dismissedIntegrationErrors, access) =>
    _(integrationErrors)
      .values()
      .compact()
      .filter((error) => {
        // Shouldn't display errors if the error is for a team rule and user is not team admin
        if (error.namespace && error.namespace.startsWith('tim:') && !isTeamAdmin(error.namespace, access)) {
          return false;
        }

        // Shouldn't display errors if the error is for a company and user is not a company admin
        if (error.namespace && error.namespace === 'global' && !isGlobalAdmin(access)) {
          return false;
        }

        const dismissedError = dismissedIntegrationErrors[error.id];

        if (!dismissedError) {
          return true;
        }

        return isTimeAboveMaxDismissal(error.lastOccurredAt, dismissedIntegrationErrors[error.id]);
      })
      .compact()
      .value(),
);

/*
 * Precogs.
 */

/** Get a map of all precogs by conversationId. */
export function getConversationPrecogs(state: AppState) {
  return state.data.conversationPrecogs;
}

export function getChatPrecogs(state: AppState) {
  return state.data.chatPrecogs;
}

export function getChatUsersOnline(state: AppState) {
  return state.data.chatUsersOnline;
}

/*
 * Custom fields.
 */

/** Get a map of all custom fields by id. */
export function getCustomFields(state: AppState): ResourceMap<CustomFieldModel> {
  return state.data.customFields;
}

/** Get a flat list of all custom fields. */
export const getFlatCustomFields = createSelector(getCustomFields, (customFields) =>
  _(customFields).values().compact().value(),
);

/*
 * Offline drafts.
 */

export function getOfflineDraftMessageUids(state: AppState) {
  return state.data.offlineDraftMessageUids;
}

export function getOfflineDraftCommentUids(state: AppState) {
  return state.data.offlineDraftCommentUids;
}

/*
 * Topics.
 */

export function getTopics(state: AppState) {
  return state.data.topics;
}

export const getFlatTopics = createSelector(getTopics, (topics) => _(topics).values().compact().value());

/*
 * Tokens.
 */

export function getApiTokens(state: AppState) {
  return state.data.apiTokens;
}

export const getFlatApiTokens = createSelector(getApiTokens, (tokens) =>
  _(tokens).values().compact().value(),
);
